import { useCallback, useRef, useState } from 'react';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { apiPost } from '../lib/api';

interface PresignedResponse {
  fileId: string;
  uploadUrl: string;
  expiresAt: string;
}

export interface UploadProgress {
  fileId: string;
  name: string;
  progress: number;
  status: 'pending' | 'uploading' | 'confirming' | 'done' | 'error' | 'conflict';
  error?: string;
}

export type ConflictResolution = { type: 'overwrite' } | { type: 'rename'; newName: string } | { type: 'cancel' };

export function useUpload(currentFolderId: string | null) {
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  // Keyed by the upload's stable tempId so a conflict can be retried later
  // (overwrite / rename) without asking the user to re-pick the file.
  const pendingFiles = useRef(new Map<string, File>());
  const qc = useQueryClient();

  const updateUpload = useCallback((id: string, patch: Partial<UploadProgress>) => {
    setUploads((prev) => prev.map((u) => (u.fileId === id ? { ...u, ...patch } : u)));
  }, []);

  const runUpload = useCallback(
    async (tempId: string, file: File, name: string, overwrite: boolean) => {
      try {
        // Step 1: get presigned URL
        const presignRes = await apiPost<PresignedResponse>('/files/presigned-upload', {
          name,
          sizeBytes: file.size,
          mimeType: file.type || 'application/octet-stream',
          folderId: currentFolderId,
          overwrite,
        });

        if (!presignRes.success || !presignRes.data) {
          if (presignRes.error?.code === 'DUPLICATE_NAME') {
            updateUpload(tempId, { status: 'conflict', name, error: undefined });
          } else {
            updateUpload(tempId, { status: 'error', error: presignRes.error?.message ?? 'Failed to start upload' });
          }
          return;
        }

        const { fileId, uploadUrl } = presignRes.data;
        pendingFiles.current.set(fileId, file);
        setUploads((prev) =>
          prev.map((u) => (u.fileId === tempId ? { ...u, fileId, name, status: 'uploading', progress: 0 } : u))
        );

        // Step 2: upload to R2 via presigned PUT URL with progress tracking
        await new Promise<void>((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.upload.addEventListener('progress', (ev) => {
            if (ev.lengthComputable) {
              updateUpload(fileId, { progress: Math.round((ev.loaded / ev.total) * 100) });
            }
          });
          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) resolve();
            else reject(new Error(`R2 upload failed: ${xhr.status}`));
          });
          xhr.addEventListener('error', () => reject(new Error('Network error during upload')));
          xhr.open('PUT', uploadUrl);
          xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
          xhr.send(file);
        });

        // Step 3: confirm upload
        updateUpload(fileId, { status: 'confirming', progress: 100 });
        const confirmRes = await apiPost('/files/confirm-upload', { fileId });

        if (confirmRes.success) {
          updateUpload(fileId, { status: 'done' });
          qc.invalidateQueries({ queryKey: ['files'] });
        } else {
          updateUpload(fileId, { status: 'error', error: confirmRes.error?.message ?? 'Confirm failed' });
        }
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.data?.error?.code === 'DUPLICATE_NAME') {
          updateUpload(tempId, { status: 'conflict', name, error: undefined });
          return;
        }
        const msg = axios.isAxiosError(err)
          ? (err.response?.data?.error?.message ?? err.message)
          : err instanceof Error
            ? err.message
            : 'Upload failed';
        updateUpload(tempId, { status: 'error', error: msg });
      }
    },
    [currentFolderId, qc, updateUpload]
  );

  const uploadFiles = useCallback(
    async (selectedFiles: File[]) => {
      for (const file of selectedFiles) {
        const tempId = `tmp-${Math.random()}`;
        pendingFiles.current.set(tempId, file);
        setUploads((prev) => [...prev, { fileId: tempId, name: file.name, progress: 0, status: 'pending' }]);
        await runUpload(tempId, file, file.name, false);
      }
    },
    [runUpload]
  );

  // Called after the user picks how to handle a same-name conflict (Explorer-style).
  const resolveConflict = useCallback(
    async (tempId: string, resolution: ConflictResolution) => {
      if (resolution.type === 'cancel') {
        pendingFiles.current.delete(tempId);
        setUploads((prev) => prev.filter((u) => u.fileId !== tempId));
        return;
      }

      const file = pendingFiles.current.get(tempId);
      if (!file) return;

      updateUpload(tempId, { status: 'pending', error: undefined });
      if (resolution.type === 'overwrite') {
        await runUpload(tempId, file, file.name, true);
      } else {
        await runUpload(tempId, file, resolution.newName, false);
      }
    },
    [runUpload, updateUpload]
  );

  const clearCompleted = useCallback(() => {
    setUploads((prev) => prev.filter((u) => u.status !== 'done' && u.status !== 'error'));
  }, []);

  return { uploads, uploadFiles, clearCompleted, resolveConflict };
}
